import { cn } from '@lib/util/cn';
import { Text } from '@modules/common/components/text';
import ChevronDown from '@modules/common/icons/chevron-down';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import type React from 'react';

type AccordionItemProps = AccordionPrimitive.AccordionItemProps & {
  title: string;
  subtitle?: string;
  description?: string;
  required?: boolean;
  tooltip?: string;
  forceMountContent?: true;
  headingSize?: 'small' | 'medium' | 'large';
  customTrigger?: React.ReactNode;
  complete?: boolean;
  active?: boolean;
  triggerable?: boolean;
  children: React.ReactNode;
};

type AccordionProps =
  | (AccordionPrimitive.AccordionSingleProps &
      React.RefAttributes<HTMLDivElement>)
  | (AccordionPrimitive.AccordionMultipleProps &
      React.RefAttributes<HTMLDivElement>);

const Accordion: React.FC<AccordionProps> & {
  Item: React.FC<AccordionItemProps>;
} = ({ children, ...props }) => {
  return (
    <AccordionPrimitive.Root {...props}>{children}</AccordionPrimitive.Root>
  );
};

const Item: React.FC<AccordionItemProps> = ({
  title,
  subtitle,
  description,
  children,
  className,
  headingSize = 'large',
  customTrigger = undefined,
  forceMountContent = undefined,
  triggerable,
  ...props
}) => {
  return (
    <AccordionPrimitive.Item
      {...props}
      className={cn(
        'border-border-semantic-subtle group border-t last:mb-0 last:border-b',
        'py-3',
        className,
      )}
    >
      <div className="px-1">
        <div className="flex flex-col">
          <AccordionPrimitive.Header asChild>
            <h2>
              <AccordionPrimitive.Trigger
                aria-label={title}
                className="group/trigger cursor-pointer flex w-full items-center justify-between text-left"
              >
                <Text className="text-brand-primary-900 text-lg font-heading font-light leading-[1.125] tracking-[-0.01em]">
                  {title}
                </Text>
                {customTrigger || <MorphingTrigger />}
              </AccordionPrimitive.Trigger>
            </h2>
          </AccordionPrimitive.Header>
          {subtitle && (
            <Text as="span" size="small" className="mt-1">
              {subtitle}
            </Text>
          )}
        </div>
      </div>
      <AccordionPrimitive.Content
        forceMount={forceMountContent}
        className={cn(
          'data-[state=closed]:animate-accordion-close data-[state=open]:animate-accordion-open data-[state=closed]:pointer-events-none px-1',
        )}
      >
        <div className="inter-base-regular group-data-[state=closed]:animate-accordion-close">
          {description && <Text>{description}</Text>}
          <div className="w-full">{children}</div>
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
};

Accordion.Item = Item;

const MorphingTrigger = () => {
  return (
    <ChevronDown
      size="24"
      className="text-fg-neutral-soft shrink-0 transition-transform duration-300 group-data-[state=open]/trigger:rotate-180"
    />
  );
};

export default Accordion;
